import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useChat } from '../hooks/useChat';
import { useAgentChat } from '../hooks/useAgentChat';
import { usePosts } from '../hooks/usePosts';
import ChatSidebar from '../components/chat/ChatSidebar';
import ChatMessages from '../components/chat/ChatMessages';
import ChatInput from '../components/chat/ChatInput';
import AgentSelector from '../components/chat/AgentSelector';
import SchedulePostModal from '../components/chat/SchedulePostModal';

function Chat() {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client') || null;

  const [selectedAgentId, setSelectedAgentId] = useState('orquestrador');
  const [progressStatus, setProgressStatus] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [urgent, setUrgent] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState(null);
  const [savingPosts, setSavingPosts] = useState(false);
  const [toast, setToast] = useState(null);

  const {
    conversations,
    messages,
    activeConversationId,
    setActiveConversation,
    createConversation,
    deleteConversation,
    addMessageLocally,
    updateConversationTitle,
  } = useChat();

  const { sendMessage, cancelStream, sending, streamingContent, pendingBatches } = useAgentChat();
  const { createPostsFromChat } = usePosts();

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const activeConv = conversations.find(c => c.id === activeConversationId);
  const activeClientId = clientId || activeConv?.client_id;

  const handleNewConversation = useCallback(async () => {
    await createConversation(selectedAgentId, clientId);
    setSidebarOpen(false);
  }, [createConversation, selectedAgentId, clientId]);

  const handleSelectConversation = useCallback((id) => {
    setActiveConversation(id);
    const conv = conversations.find(c => c.id === id);
    if (conv) {
      setSelectedAgentId(conv.agent_id);
    }
    setSidebarOpen(false);
  }, [setActiveConversation, conversations]);

  const handleSchedulePost = useCallback((msg) => {
    if (!activeClientId) {
      showToast('error', 'Nenhum cliente associado a esta conversa. Abra o chat a partir de um cliente.');
      return;
    }
    setScheduleMessage(msg);
    setShowScheduleModal(true);
  }, [activeClientId]);

  const handleSubmitDates = useCallback(async (dates) => {
    if (!activeClientId || !scheduleMessage) return;
    setSavingPosts(true);
    try {
      const created = await createPostsFromChat(activeClientId, dates, scheduleMessage.content);
      setShowScheduleModal(false);
      setScheduleMessage(null);
      showToast('success', `${created.length} post${created.length > 1 ? 's' : ''} enviado${created.length > 1 ? 's' : ''} para aprovacao do cliente!`);
    } catch (err) {
      showToast('error', `Erro ao enviar: ${err.message}`);
    } finally {
      setSavingPosts(false);
    }
  }, [activeClientId, scheduleMessage, createPostsFromChat]);

  const handleSend = useCallback(async (text) => {
    let convId = activeConversationId;

    if (!convId) {
      const conv = await createConversation(selectedAgentId, clientId);
      if (!conv) return;
      convId = conv.id;
    }

    const userMsg = {
      id: `temp-${Date.now()}`,
      conversation_id: convId,
      role: 'user',
      content: text,
      agent_id: null,
      created_at: new Date().toISOString(),
    };
    addMessageLocally(userMsg);

    const isFirstMessage = messages.length === 0;
    if (isFirstMessage) {
      const title = text.length > 50 ? text.substring(0, 47) + '...' : text;
      updateConversationTitle(convId, title);
    }

    setProgressStatus(null);

    sendMessage({
      message: text,
      agentId: selectedAgentId,
      conversationId: convId,
      clientId,
      urgent,
      onChunk: (_chunk, _full, status) => {
        if (status) setProgressStatus(status);
      },
      onComplete: (fullContent) => {
        const assistantMsg = {
          id: `temp-resp-${Date.now()}`,
          conversation_id: convId,
          role: 'assistant',
          content: fullContent,
          agent_id: selectedAgentId,
          created_at: new Date().toISOString(),
        };
        addMessageLocally(assistantMsg);
        setProgressStatus(null);
      },
      onError: (errorMsg) => {
        const errorMsgObj = {
          id: `temp-err-${Date.now()}`,
          conversation_id: convId,
          role: 'assistant',
          content: `**Erro:** ${errorMsg}`,
          agent_id: selectedAgentId,
          created_at: new Date().toISOString(),
        };
        addMessageLocally(errorMsgObj);
        setProgressStatus(null);
      },
      onBatchSubmitted: (data) => {
        const batchMsg = {
          id: `temp-batch-${Date.now()}`,
          conversation_id: convId,
          role: 'assistant',
          content: '',
          agent_id: selectedAgentId,
          created_at: new Date().toISOString(),
          metadata: { batch_id: data.batch_id, batch_status: 'processing' },
        };
        addMessageLocally(batchMsg);
        setProgressStatus(null);
      },
    });
  }, [activeConversationId, selectedAgentId, clientId, urgent, messages.length, createConversation, addMessageLocally, updateConversationTitle, sendMessage]);

  return (
    <div className={`chat-page ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <ChatSidebar
        conversations={conversations}
        activeConversationId={activeConversationId}
        onSelect={handleSelectConversation}
        onNew={handleNewConversation}
        onDelete={deleteConversation}
      />
      <div className="chat-main">
        <AgentSelector
          selectedAgentId={selectedAgentId}
          onSelect={setSelectedAgentId}
        />
        <ChatMessages
          messages={messages}
          streamingContent={streamingContent}
          sending={sending}
          progressStatus={progressStatus}
          pendingBatches={pendingBatches}
          onSchedulePost={activeClientId ? handleSchedulePost : null}
        />
        <ChatInput
          onSend={handleSend}
          onCancel={cancelStream}
          sending={sending}
          disabled={false}
          urgent={urgent}
          onToggleUrgent={() => setUrgent(prev => !prev)}
        />
      </div>

      {showScheduleModal && (
        <SchedulePostModal
          onClose={() => { setShowScheduleModal(false); setScheduleMessage(null); }}
          onSubmit={handleSubmitDates}
          saving={savingPosts}
          messageContent={scheduleMessage?.content || ''}
        />
      )}

      {toast && (
        <div className="toast-container">
          <div className={`toast toast--${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
}

export default Chat;
